import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { Storage } from '@d-constants';
import { environment } from '@d-environments/environment';
import {
  ICredentials,
  ISSOCredentials,
  UserModel,
} from '@d-models/other/user.model';
import { HttpService } from '@d-services/other/http.service';
import { HttpClient } from '@angular/common/http';
import {
  ICheckEmailRequest,
  ICheckEmailResponse,
  ILoginResponse,
  IRefreshTokenResponse,
} from '@d-models/services/wms-auth.model';
import { BaseResponseModel } from '@d-models';

export interface ILoginProps {
  username: string;
  password: string;
}

@Injectable()
export class AuthService {
  authUrl = `${environment.API_URL}/auth-svc`; // URL to web api

  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<any>(null);

  private currentUser$ = new BehaviorSubject<UserModel>(null);
  private isGettingCurrentUser: boolean = false;

  constructor(private http: HttpClient) {}

  login(params: ICredentials) {
    return this.http
      .post<BaseResponseModel<ILoginResponse>>(
        `${this.authUrl}/auth/login`,
        params,
      )
      .pipe(
        tap((res) => {
          if (res.isSuccess) {
            this.setAccessToken(res.data.accessToken);
            this.setRefreshToken(res.data.refreshToken);
          }
        }),
      );
  }

  loginSSO(params: ISSOCredentials) {
    return this.http
      .post<BaseResponseModel<ILoginResponse>>(
        `${this.authUrl}/auth/login-sso`,
        params,
      )
      .pipe(
        tap((res) => {
          if (res.isSuccess) {
            this.setAccessToken(res.data.accessToken);
            this.setRefreshToken(res.data.refreshToken);
          }
        }),
      );
  }

  checkEmail(params: ICheckEmailRequest) {
    return this.http.post<BaseResponseModel<ICheckEmailResponse>>(
      `${this.authUrl}/auth/check-email`,
      params,
    );
  }

  refreshAccessToken() {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.asObservable();
    } else {
      this.refreshTokenInProgress = true;
      return this.http
        .post<BaseResponseModel<IRefreshTokenResponse>>(
          `${this.authUrl}/auth/refresh-token`,
          {
            refreshToken: this.getRefreshToken(),
          },
        )
        .pipe(
          tap((res) => {
            const { accessToken } = res.data;
            this.setAccessToken(accessToken);
          }),
          switchMap((res) => {
            const { accessToken } = res.data;

            this.refreshTokenSubject.next(accessToken);
            this.refreshTokenInProgress = false;
            return this.refreshTokenSubject.asObservable();
          }),
          catchError((err) => {
            this.refreshTokenInProgress = false;
            return throwError(() => err);
          }),
        );
    }
  }

  getCurrentUser(): Observable<UserModel> {
    if (this.currentUser$.value) {
      return this.currentUser$.asObservable();
    } else {
      if (this.isGettingCurrentUser) {
        return this.currentUser$.asObservable();
      }
      this.isGettingCurrentUser = true;
      return this.http
        .get<BaseResponseModel<UserModel>>(
          `${this.authUrl}/member/account/profile`,
        )
        .pipe(
          map((res) => {
            const userInfo = res.data;
            if (res.isSuccess) {
              this.isGettingCurrentUser = false;
              this.currentUser$.next(userInfo);
            }
            return userInfo;
          }),
        );
    }
  }

  getCurrentRolePermissions(): Observable<any> {
    return this.http.get<any>(
      `${this.authUrl}/role/get-current-user-permissions`,
    );
  }

  setAccessToken = (token: string) => {
    localStorage.setItem(Storage.ACCESS_TOKEN, token);
  };

  setRefreshToken = (token: string) => {
    localStorage.setItem(Storage.REFRESH_TOKEN, token);
  };

  getAccessToken = (): string => {
    return localStorage.getItem(Storage.ACCESS_TOKEN);
  };

  getRefreshToken = (): string => {
    return localStorage.getItem(Storage.REFRESH_TOKEN);
  };

  getAuthorizationToken() {
    return localStorage.getItem(Storage.JWT);
  }

  //Todo: check Expiration
  public isLoggedIn() {
    return !!localStorage.getItem(Storage.JWT);
  }

  public logout() {
    localStorage.clear();
    this.currentUser$.next(null);
  }
}
